import "./InvestmentDirectory.css";

import { Link } from "react-router-dom";

import { DropdownLightIcon } from "../../../assets";
import { AssetManagerTemplate } from "../../../templates/AssetManagerTemplate";
import { getNewDealRoute } from "../../../utils/routes";
import { Column, CursorPagination, Row, Select } from "../../../widget";
import { Autocomplete, Button, Slider, TextInput } from "../../../widget/forms";
import { ProjectCard } from "./components";
import { useInvestmentDirectory } from "./useInvestmentDirectory";

const InvestmentDirectory = (): JSX.Element => {
  const {
    currentFilter,
    hasActiveFilters,
    totalCount,
    currentSort,
    sorting,
    projects,
    projectTypeOptions,
    developerOptions,
    standardOptions,
    phaseOptions,
    regionOptions,
    vintageRange,
    onFilterChange,
    onClearFilters,
    onSortingChange,
    onPrevious,
    onNext,
    hasNext,
    hasPrevious,
    vintageSelectorRef,
    showVintageSlider,
    setShowVintageSlider,
    onClearFilter,
  } = useInvestmentDirectory();

  return (
    <AssetManagerTemplate activeElement="Portfolio">
      <div className="InvestmentDirectory">
        <Row spacingV="ml">
          <Column span={12}>
            <h1>Investment directory</h1>
          </Column>
        </Row>
        <Row spacingV="l">
          <Column span={12}>
            <p className="body1 InvestmentDirectoryInfo">
              If you are looking to invest in a project not shown on the investment directory, you can create your own
              project when you <Link to={getNewDealRoute()}>create a new deal</Link>.
            </p>
          </Column>
        </Row>
        <div className="InvestmentDirectoryFilters">
          <div className="InvestmentDirectoryFiltersHeader">
            <h3>Filters</h3>
          </div>
          <div className="InvestmentDirectoryFiltersControls">
            <div className="InvestmentDirectoryFiltersControlWrapper InvestmentDirectoryFiltersSearchWrapper">
              <TextInput
                name="search"
                placeholder="Search projects & landholdings"
                value={currentFilter.search || ""}
                onChange={(value) => onFilterChange("search", value || null)}
              />
              {currentFilter.search && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("search")}
                />
              )}
            </div>
            <div className="InvestmentDirectoryFiltersControlWrapper">
              {projectTypeOptions && (
                <Autocomplete
                  className="InvestmentDirectoryFiltersControl ProjectType"
                  placeholder="Project type"
                  data={projectTypeOptions}
                  initialKey={currentFilter.projectType || undefined}
                  onSelect={(value) => onFilterChange("projectType", value || null)}
                  corners="rounded"
                  fontSize="body2"
                  size="lm"
                  dropdownMatchSelectWidth={false}
                />
              )}
              {currentFilter.projectType && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("projectType")}
                />
              )}
            </div>
            <div className="InvestmentDirectoryFiltersControlWrapper">
              <Autocomplete
                className="InvestmentDirectoryFiltersControl Developer"
                placeholder="Developer"
                data={developerOptions}
                initialKey={currentFilter.developer || undefined}
                onSelect={(value) => onFilterChange("developer", value || null)}
                corners="rounded"
                fontSize="body2"
                size="lm"
                dropdownMatchSelectWidth={false}
              />
              {currentFilter.developer && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("developer")}
                />
              )}
            </div>
            <div className="InvestmentDirectoryFiltersControlWrapper InvestmentDirectoryFiltersVintageWrapper">
              <div className="InvestmentDirectoryFiltersVintage">
                <button
                  type="button"
                  className={`InvestmentDirectoryFiltersVintageTrigger ${currentFilter.vintage ? "active" : ""}`}
                  onClick={() => setShowVintageSlider(!showVintageSlider)}
                >
                  <p className="body2">
                    {currentFilter.vintage
                      ? `Vintage: ${currentFilter.vintage[0]} - ${currentFilter.vintage[1]}`
                      : "Vintage"}
                  </p>
                  <DropdownLightIcon className="InvestmentDirectoryFiltersVintageIcon" />
                </button>
                {showVintageSlider && vintageRange && (
                  <div ref={vintageSelectorRef} className="InvestmentDirectoryFiltersVintageSlider">
                    <Slider
                      range
                      value={currentFilter.vintage || [vintageRange.min, vintageRange.max]}
                      min={vintageRange.min}
                      max={vintageRange.max}
                      onChange={(value) => onFilterChange("vintage", value as number[])}
                      formatValue={(value) => value?.toString()}
                    />
                  </div>
                )}
              </div>
              {currentFilter.vintage && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("vintage")}
                />
              )}
            </div>
            <div className="InvestmentDirectoryFiltersControlWrapper">
              <Autocomplete
                className="InvestmentDirectoryFiltersControl Standard"
                placeholder="Standard"
                data={standardOptions}
                initialKey={currentFilter.standard || undefined}
                onSelect={(value) => onFilterChange("standard", value || null)}
                corners="rounded"
                fontSize="body2"
                size="lm"
                dropdownMatchSelectWidth={false}
              />
              {currentFilter.standard && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("standard")}
                />
              )}
            </div>
            <div className="InvestmentDirectoryFiltersControlWrapper">
              <Autocomplete
                className="InvestmentDirectoryFiltersControl Phase"
                placeholder="Phase"
                data={phaseOptions}
                initialKey={currentFilter.phase || undefined}
                onSelect={(value) => onFilterChange("phase", value || null)}
                corners="rounded"
                fontSize="body2"
                size="lm"
                dropdownMatchSelectWidth={false}
              />
              {currentFilter.phase && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("phase")}
                />
              )}
            </div>
            <div className="InvestmentDirectoryFiltersControlWrapper">
              <Autocomplete
                className="InvestmentDirectoryFiltersControl Region"
                placeholder="Region"
                data={regionOptions}
                initialKey={currentFilter.region || undefined}
                onSelect={(value) => onFilterChange("region", value || null)}
                corners="rounded"
                fontSize="body2"
                size="lm"
                dropdownMatchSelectWidth={false}
              />
              {currentFilter.region && (
                <Button
                  className="InvestmentDirectoryFiltersClearButton"
                  variant="secondary"
                  size="extraSmall"
                  minWidth={false}
                  text="Clear"
                  onClick={() => onClearFilter("region")}
                />
              )}
            </div>
            {hasActiveFilters && (
              <Button
                className="ClearAllButton"
                variant="secondary"
                text="Clear all"
                size="small"
                onClick={() => {
                  onClearFilters();
                  setShowVintageSlider(false);
                }}
              />
            )}
            <div className="InvestmentDirectoryHeaderSortingContainer">
              <span className="body2">Sort by:</span>
              <Select
                className="InvestmentDirectoryHeaderSortingContainerSelect"
                value={currentSort}
                data={sorting}
                dropdownMatchSelectWidth={false}
                onChange={onSortingChange}
                corners="rounded"
              />
            </div>
          </div>
        </div>
        {projects.map((p) => (
          <Row key={p.uuid} spacingV="ml">
            <Column span={12}>
              <ProjectCard
                isLandholdingProject={false}
                className="InvestmentDirectoryProjectCard"
                imageSource={p.imageSource}
                uuid={p.uuid}
                name={p.name}
                addressCountryCode={p.addressCountryCode}
                addressRegion={p.addressRegion}
                developerSmallLogoFileUrl={p.developerSmallLogoFileUrl}
                developerDisplayName={p.developerDisplayName}
                standardDisplayName={p.standardDisplayName}
                cachedAvailableQuantity={p.cachedAvailableQuantity}
                status={p.status}
                phase={p.phase}
                projectType={p.projectType}
                projectTypeUuid={p.projectTypeUuid}
                areaNetHa={p.areaNetHa}
                avgPrice={p.avgPrice}
                tco2e={p.tco2e}
                landholding={p.landholdings[0]}
              />
            </Column>
          </Row>
        ))}
        <Row spacingV="sl">
          <Column span={12} justify="end">
            <p className="body2">
              {totalCount} result{totalCount !== 1 ? "s" : ""}
            </p>
          </Column>
        </Row>
        <CursorPagination onPrevious={onPrevious} onNext={onNext} hasNext={hasNext} hasPrevious={hasPrevious} />
      </div>
    </AssetManagerTemplate>
  );
};

export default InvestmentDirectory;
